import { DisplayGame, PlayerData, PlayerProp, PlayerPropSportsbook, PropsApiResponse, PropsOutcome, SportsbookOutcome, TeamInfo } from './types';
import { americanToImpliedProb } from './probability';
import { getTeamInfo, NBA_TEAM_NAME_TO_ABBR } from './nbaTeams';
import { getNhlTeamInfo, NHL_TEAM_NAME_TO_ABBR } from './nhlTeams';
import { getMLBTeamInfo, MLB_TEAM_NAME_TO_ABBR } from './mlbTeams';
import { getNFLTeamInfo, NFL_TEAM_NAME_TO_ABBR, NFL_TEAM_ABBR_NORMALIZE } from './nflTeams';
import { getStatDecimals } from './statLabels';

const DEFAULT_AVATAR = 'https://awesemostorage.blob.core.windows.net/assets/silhouette.png';
const PITCHER_STATS = new Set(['K', 'ER', 'OUTS', 'HA', 'IP', 'PBB']);

// League-aware team info lookup
function getTeamInfoForLeague(abbr: string, league: string): TeamInfo {
  switch (league.toUpperCase()) {
    case 'NHL':
      return getNhlTeamInfo(abbr);
    case 'MLB':
      return getMLBTeamInfo(abbr);
    case 'NFL':
      return getNFLTeamInfo(abbr);
    case 'NBA':
    default:
      return getTeamInfo(abbr);
  }
}

// Injury status badge colors and labels
export interface InjuryBadge {
  label: string;
  bgColor: string;
  textColor: string;
}

export function getInjuryBadge(status?: string): InjuryBadge | null {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === 'out') return { label: 'OUT', bgColor: '#d32f2f', textColor: '#fff' };
  if (s === 'doubtful') return { label: 'DBTF', bgColor: '#f57c00', textColor: '#fff' };
  if (s === 'questionable') return { label: 'QUES', bgColor: '#fbc02d', textColor: '#000' };
  if (s === 'probable') return { label: 'PROB', bgColor: '#388e3c', textColor: '#fff' };
  return null;
}

// Team name to abbreviation mappings. Full names are unique across leagues but
// nicknames collide (Giants, Cardinals, Panthers, Jets...), so look up the active
// league's map first and only then fall back to the combined map.
const TEAM_NAME_TO_ABBR_BY_LEAGUE: Record<string, Record<string, string>> = {
  NBA: NBA_TEAM_NAME_TO_ABBR,
  NHL: NHL_TEAM_NAME_TO_ABBR,
  MLB: MLB_TEAM_NAME_TO_ABBR,
  NFL: NFL_TEAM_NAME_TO_ABBR,
};
const TEAM_NAME_TO_ABBR: Record<string, string> = {
  ...NBA_TEAM_NAME_TO_ABBR,
  ...NHL_TEAM_NAME_TO_ABBR,
  ...MLB_TEAM_NAME_TO_ABBR,
  ...NFL_TEAM_NAME_TO_ABBR,
};

// Normalize stat names from API to display format
const STAT_NORMALIZE: Record<string, string> = {
  // NBA stats
  'Points': 'PTS',
  'Rebounds': 'REB',
  'Assists': 'AST',
  '3-Pointers': '3PM',
  'Three Pointers': '3PM',
  'Three Pointers Made': '3PM',
  'Threes': '3PM',
  'Steals': 'STL',
  'Blocks': 'BLK',
  'Pts + Reb + Ast': 'PRA',
  'Points + Rebounds + Assists': 'PRA',
  'Points + Rebounds': 'P+R',
  'Points + Assists': 'P+A',
  'Rebounds + Assists': 'R+A',
  'PRA': 'PRA',
  // NHL stats
  'Total Points': 'PTS',
  'Total Assists': 'AST',
  'Total Player Goals': 'G',
  'Total Shots on Goal': 'SOG',
  'Blocked Shots': 'BLK',
  'Shots on Goal': 'SOG',
  'Goals': 'G',
  'Player Goals': 'G',
  'Saves': 'SV',
  'Goalie Saves': 'SV',
  'Power Play Points': 'PPP',
  // MLB hitter stats
  'Hits': 'H',
  'Total Hits': 'H',
  'Home Runs': 'HR',
  'Total Home Runs': 'HR',
  'Runs Scored': 'R',
  'Total Player Runs': 'R',
  'Runs Batted In': 'RBI',
  'Total RBI': 'RBI',
  'Total RBIs': 'RBI',
  'Total Walks': 'BB',
  'Total Batter Walks': 'BB',
  'Batter Strikeouts': 'SO',
  'Total Batter Strikeouts': 'SO',
  'Stolen Bases': 'SB',
  'Total Stolen Bases': 'SB',
  'Total Bases': 'TB',
  'Singles': '1B',
  'Total Singles': '1B',
  'Doubles': '2B',
  'Total Doubles': '2B',
  'Triples': '3B',
  'Total Triples': '3B',
  // MLB pitcher stats
  'Innings Pitched': 'IP',
  'Pitcher Strikeouts': 'K',
  'Total Strikeouts': 'K',
  'Strikeouts Pitched': 'K',
  'Earned Runs': 'ER',
  'Earned Runs Allowed': 'ER',
  'Total Earned Runs': 'ER',
  'Hits Allowed': 'HA',
  'Pitcher Hits Allowed': 'HA',
  'Total Hits Allowed': 'HA',
  'Total Pitcher Walks': 'PBB',
  'Pitcher Walks': 'PBB',
  'Total Pitching Outs': 'OUTS',
  'Pitching Outs': 'OUTS',
  // NFL stats
  'Passing Yards': 'Pass Yds',
  'Passing Yds': 'Pass Yds',
  'Pass Yards': 'Pass Yds',
  'Passing Touchdowns': 'Pass TDs',
  'Passing TDs': 'Pass TDs',
  'Pass Completions': 'Comp',
  'Passing Completions': 'Comp',
  'Completions': 'Comp',
  'Passing Attempts': 'Pass Att',
  'Pass Attempts': 'Pass Att',
  'Interceptions': 'INT',
  'Interceptions Thrown': 'INT',
  'Rushing Yards': 'Rush Yds',
  'Rushing Yds': 'Rush Yds',
  'Rush Yards': 'Rush Yds',
  'Rushing Attempts': 'Rush Att',
  'Rush Attempts': 'Rush Att',
  'Rushing Touchdowns': 'Rush TDs',
  'Receptions': 'Rec',
  'Player Receptions': 'Rec',
  'Recs': 'Rec',
  'Receiving Yards': 'Rec Yds',
  'Receiving Yds': 'Rec Yds',
  'Rec Yards': 'Rec Yds',
  'Receiving Touchdowns': 'Rec TDs',
  'Receiving Targets': 'Targets',
  'Longest Reception': 'Long Rec',
  'Longest Rush': 'Long Rush',
  'Rushing + Receiving Yards': 'Rush+Rec Yds',
  'Rush + Rec Yards': 'Rush+Rec Yds',
  'Passing + Rushing Yards': 'Pass+Rush Yds',
  'Pass + Rush Yards': 'Pass+Rush Yds',
  'Anytime Touchdown Scorer': 'Any TD',
  'Any Time Touchdown Scorer': 'Any TD',
  'Anytime TD Scorer': 'Any TD',
  'Player Anytime TD': 'Any TD',
  'Kicking Points': 'Kick Pts',
  'Field Goals Made': 'FGM',
  'Extra Points Made': 'XPM',
  'Tackles + Assists': 'Tkl+Ast',
  'Tackles': 'Tackles',
  'Sacks': 'Sacks',
};

// NFL market names exactly as the OddsShopper offers table emits them (verified against
// GET /api/leagues/NFL/offers on 2026-09-03). Consulted before the shared map because some
// names collide across leagues: "Total Assists" is a defensive stat in the NFL, NHL assists elsewhere.
const NFL_STAT_NORMALIZE: Record<string, string> = {
  // Passing
  'Total Passing Yards': 'Pass Yds',
  'Total Passing Touchdowns': 'Pass TDs',
  'Total Pass Completions': 'Comp',
  'Total Passing Attempts': 'Pass Att',
  'Total Interceptions Thrown': 'INT',
  'Longest Passing Completion': 'Long Comp',
  // Rushing
  'Total Rushing Yards': 'Rush Yds',
  'Total Rushing Attempts': 'Rush Att',
  'Total Rushing Touchdowns': 'Rush TDs',
  'Longest Rush': 'Long Rush',
  // Receiving
  'Total Receptions': 'Rec',
  'Total Receiving Yards': 'Rec Yds',
  'Total Receiving Touchdowns': 'Rec TDs',
  'Total Targets': 'Targets',
  'Longest Reception': 'Long Rec',
  // Combos / scoring
  'Total Rushing + Receiving Yards': 'Rush+Rec Yds',
  'Total Passing + Rushing Yards': 'Pass+Rush Yds',
  'Total Touchdowns': 'Any TD',
  'Total Rushing + Receiving TDs': 'Rush+Rec TDs',
  'Total Passing + Rushing + Receiving TDs': 'Total TDs',
  'Fantasy Points': 'Fantasy Pts',
  // Kicking
  'Total Kicking Points': 'Kick Pts',
  'Total Field Goals Made': 'FGM',
  'Total Extra Points Made': 'XPM',
  // Defense
  'Total Tackles + Assists': 'Tkl+Ast',
  'Tackles + Assists': 'Tkl+Ast',
  'Total Tackles': 'Tackles',
  'Total Assists': 'Def Ast',
  'Total Sacks': 'Sacks',
  'Caught INTs': 'INT Caught',
  'Fumbles Lost': 'Fum Lost',
};

// Stat to projection key mapping
const STAT_TO_PROJ_KEY: Record<string, keyof PlayerData['projections']> = {
  'PTS': 'pts',
  'REB': 'reb',
  'AST': 'ast',
  '3PM': 'threes',
  'PRA': 'pra',
};

// Normalize team abbreviation variants to a standard form
// This handles cases where the API uses different abbreviations than our mappings
const TEAM_ABBR_NORMALIZE: Record<string, string> = {
  // NHL variants
  'WAS': 'WSH',  // Washington Capitals
  'WIN': 'WPG',  // Winnipeg Jets
  'LOS': 'LA',   // Los Angeles Kings
  'NJD': 'NJ',   // New Jersey Devils
  'TAM': 'TB',   // Tampa Bay Lightning
  'SJS': 'SJ',   // San Jose Sharks
  'VEG': 'VGK',  // Vegas Golden Knights
  // NBA variants (if any)
  'PHO': 'PHX',  // Phoenix Suns
  'BRK': 'BKN',  // Brooklyn Nets
  'NOR': 'NO',   // New Orleans Pelicans
  'SAN': 'SA',   // San Antonio Spurs
  'NYK': 'NY',   // New York Knicks
  'UTA': 'UTA',  // Utah (same)
  'GSW': 'GS',   // Golden State Warriors
};

// NFL shares abbreviations with the NBA/NHL variant table (WAS, NOR, TAM, LA...) but
// means different teams by them, so it gets its own normalization table.
function normalizeTeamAbbr(abbr: string, league: string = 'NBA'): string {
  const upper = abbr.toUpperCase();
  if (league.toUpperCase() === 'NFL') {
    return NFL_TEAM_ABBR_NORMALIZE[upper] || upper;
  }
  return TEAM_ABBR_NORMALIZE[upper] || upper;
}

function getTeamAbbr(teamName: string, league: string = 'NBA'): string {
  // API returns abbreviations directly for outcome.team (e.g., "CLE", "BOS")
  // Only use mapping for full names in matchup strings
  if (teamName.length <= 4 && teamName === teamName.toUpperCase()) {
    return normalizeTeamAbbr(teamName, league); // Normalize the abbreviation
  }
  const leagueMap = TEAM_NAME_TO_ABBR_BY_LEAGUE[league.toUpperCase()];
  const abbr = leagueMap?.[teamName] || TEAM_NAME_TO_ABBR[teamName] || teamName.substring(0, 3).toUpperCase();
  return normalizeTeamAbbr(abbr, league);
}

// Dev-only guard: an unmapped market renders under its raw feed name, so shout once per stat.
const warnedUnmappedStats = new Set<string>();
function warnUnmappedStat(stat: string, league: string): void {
  if (process.env.NODE_ENV === 'production' || warnedUnmappedStats.has(stat)) return;
  warnedUnmappedStats.add(stat);
  console.warn(
    `[Props] Unmapped ${league} market "${stat}" — add it to STAT_NORMALIZE (transformPropsData.ts) and STAT_LABELS (statLabels.ts).`
  );
}

// Case-insensitive views of the stat maps: dictionary keys arrive camel-cased from the API
// ("Total Rushing Yards" -> "total Rushing Yards"), so an exact lookup would miss.
const lowerKeyed = (map: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(map).map(([k, v]) => [k.toLowerCase(), v]));
const NFL_STAT_NORMALIZE_CI = lowerKeyed(NFL_STAT_NORMALIZE);
const STAT_NORMALIZE_CI = lowerKeyed(STAT_NORMALIZE);

function normalizeStat(stat: string, league: string = 'NBA'): string {
  const isNfl = league.toUpperCase() === 'NFL';
  const leagueSpecific = isNfl ? NFL_STAT_NORMALIZE[stat] ?? NFL_STAT_NORMALIZE_CI[stat.toLowerCase()] : undefined;
  const normalized = leagueSpecific || STAT_NORMALIZE[stat] || STAT_NORMALIZE_CI[stat.toLowerCase()];
  if (normalized) return normalized;
  warnUnmappedStat(stat, league);
  // Keep unmapped markets distinct (a 3-letter code would fold every "Total ..." market into "TOT")
  return stat;
}

function generatePlayerId(playerName: string): string {
  return playerName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-');
}

function generateGameId(awayAbbr: string, homeAbbr: string): string {
  return `${awayAbbr.toLowerCase()}-${homeAbbr.toLowerCase()}`;
}

function parseMatchup(matchup: string, league: string = 'NBA'): { awayTeam: string; homeTeam: string } {
  // Format: "Cleveland Cavaliers @ Philadelphia 76ers"
  const parts = matchup.split(' @ ');
  if (parts.length === 2) {
    return {
      awayTeam: getTeamAbbr(parts[0].trim(), league),
      homeTeam: getTeamAbbr(parts[1].trim(), league),
    };
  }
  // Fallback: try to parse abbreviated format
  const abbrParts = matchup.split('@');
  if (abbrParts.length === 2) {
    return {
      awayTeam: normalizeTeamAbbr(abbrParts[0].trim(), league),
      homeTeam: normalizeTeamAbbr(abbrParts[1].trim(), league),
    };
  }
  return { awayTeam: 'UNK', homeTeam: 'UNK' };
}

/** Kickoff label as the game chips show it: bare time, weekday-prefixed for the NFL's multi-day slate. */
export function formatEventTime(isoString: string, league: string = 'NBA'): string {
  try {
    const date = new Date(isoString);
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    // An NFL slate spans Thu-Mon, so a bare clock time can't tell games apart.
    if (league.toUpperCase() === 'NFL') {
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
      return `${weekday} ${time}`;
    }
    return time;
  } catch {
    return 'TBD';
  }
}

const SPORTSBOOK_LOGOS: Record<string, string> = {
  'bally': 'assets/sportsbook/icons/bally.png',
  'bet365': 'assets/sportsbook/icons/bet365.png',
  'betcris': 'assets/sportsbook/icons/betcris.png',
  'betmgm': 'assets/sportsbook/icons/betmgm.png',
  'betonline': 'assets/sportsbook/icons/betonline.png',
  'betparx': 'assets/sportsbook/icons/betparx.png',
  'betr': 'assets/sportsbook/icons/betr.png',
  'betrivers': 'assets/sportsbook/icons/betrivers.png',
  'bovada': 'assets/sportsbook/icons/bovada.png',
  'borgata': 'assets/sportsbook/icons/borgata.png',
  'bookmaker': 'assets/sportsbook/icons/bookmaker.png',
  'caesars': 'assets/sportsbook/icons/caesars.png',
  'circa': 'assets/sportsbook/icons/circa.png',
  'coolbet': 'assets/sportsbook/icons/coolbet.png',
  'dabble': 'assets/sportsbook/icons/dabble.png',
  'draftkings': 'assets/sportsbook/icons/draftkings.png',
  'espnbet': 'assets/sportsbook/icons/espnbet.png',
  'fanatics': 'assets/sportsbook/icons/fanatics.png',
  'fanduel': 'assets/sportsbook/icons/fanduel.png',
  'fliff': 'assets/sportsbook/icons/fliff.png',
  'hardrock': 'assets/sportsbook/icons/hardrock.png',
  'hardrocknj': 'assets/sportsbook/icons/hardrock.png',
  'kalshi': 'assets/sportsbook/icons/kalshi.png',
  'leovegas': 'assets/sportsbook/icons/leovegas.png',
  'northstarbetson': 'assets/sportsbook/icons/northstarbets.png',
  'polymarket': 'assets/sportsbook/icons/polymarket.png',
  'novig': 'assets/sportsbook/icons/novig.png',
  'onyxodds': 'assets/sportsbook/icons/onyxodds.png',
  'pick6': 'assets/sportsbook/icons/pick6.png',
  'pinnacle': 'assets/sportsbook/icons/pinnacle.png',
  'pointsbet': 'assets/sportsbook/icons/pointsbet.png',
  'prizepicks': 'assets/sportsbook/icons/prizepicks.png',
  'prophetx': 'assets/sportsbook/icons/prophetx.png',
  'rebet': 'assets/sportsbook/icons/rebet.png',
  'resortsworldbet': 'assets/sportsbook/icons/resortsworldbet.png',
  'sleeper': 'assets/sportsbook/icons/sleeper.png',
  'sportsinteraction': 'assets/sportsbook/icons/sportsinteraction.png',
  'stnsports': 'assets/sportsbook/icons/stnsports.png',
  'sugarhouse': 'assets/sportsbook/icons/sugarhouse.png',
  'superbook': 'assets/sportsbook/icons/superbook.png',
  'tipico': 'assets/sportsbook/icons/tipico.png',
  'thescore': 'assets/sportsbook/icons/thescore.png',
  'underdog': 'assets/sportsbook/icons/underdog.png',
  'unibet': 'assets/sportsbook/icons/unibet.png',
  'wynnbet': 'assets/sportsbook/icons/wynnbet.png',
  // Regional/rebranded books that share an icon with their parent, or whose image filename differs from
  // the odds-feed code. Without these the constructed fallback URL 404s and the logo silently disappears
  // (the img onError hides it), leaving a blank cell next to the best line.
  'bet105': 'assets/sportsbook/icons/bet105.png',
  'bet99on': 'assets/sportsbook/icons/bet99.png',
  'cryptocom': 'assets/sportsbook/icons/crypto_com.png',
  'hardrockoh': 'assets/sportsbook/icons/hardrock.png',
  'ladbrokes': 'assets/sportsbook/icons/ladbrokes.png',
  'polymarketus': 'assets/sportsbook/icons/polymarket.png',
  'stake': 'assets/sportsbook/icons/stake.png',
  'thunderpick': 'assets/sportsbook/icons/thunderpick.png',
};

function getSportsbookLogo(code: string): string {
  const imagePath = SPORTSBOOK_LOGOS[code.toLowerCase()];
  if (imagePath) {
    return `https://awesemostorage.blob.core.windows.net/${imagePath}`;
  }
  // Fallback: construct URL from code
  return `https://awesemostorage.blob.core.windows.net/assets/sportsbook/icons/${code.toLowerCase()}.png`;
}

function findBestSportsbook(sportsbooks: SportsbookOutcome[]): SportsbookOutcome | null {
  if (sportsbooks.length === 0) return null;

  // Find the sportsbook with the highest EV
  return sportsbooks.reduce((best, current) => {
    if (!best) return current;
    return current.ev > best.ev ? current : best;
  }, null as SportsbookOutcome | null);
}

export function convertSportsbookOutcome(sb: SportsbookOutcome): PlayerPropSportsbook {
  const americanOddsNum = sb.americanOdds
    ? parseInt(sb.americanOdds.replace('+', ''), 10)
    : (sb.odds >= 2 ? Math.round((sb.odds - 1) * 100) : Math.round(-100 / (sb.odds - 1)));

  // blendedEV is the EV percentage
  const evPct = (sb as any).blendedEV ?? 0;

  return {
    book: sb.code,
    bookIcon: getSportsbookLogo(sb.code),
    line: sb.line,
    odds: sb.odds,
    americanOdds: americanOddsNum >= 0 ? `+${americanOddsNum}` : `${americanOddsNum}`,
    delta: sb.delta,
    edge: evPct,
    bookWin: americanToImpliedProb(americanOddsNum),
    modelWin: (sb as any).blendedTrueWinProbability ?? 0,
    marketWin: sb.marketTrueWinProbability ?? 0,
    stokWin: sb.sharpTrueWinProbability ?? 0,
    evPct: evPct * 100,
    side: sb.side as 'Over' | 'Under',
    deepLinkUrl: sb.deepLinkUrl,
    oddsAge: sb.oddsAge,
  };
}

function createPropFromOutcome(outcome: PropsOutcome, allSportsbooks: SportsbookOutcome[], league: string = 'NBA'): PlayerProp {
  const normalizedStat = normalizeStat(outcome.stat, league);

  // Convert all sportsbooks, filter to only those with market projections, then sort by EV (highest first)
  const sportsbooks = allSportsbooks
    .map(convertSportsbookOutcome)
    .filter((sb) => sb.marketWin && sb.marketWin > 0)
    .sort((a, b) => b.evPct - a.evPct);

  return {
    stat: normalizedStat,
    originalStat: outcome.stat, // Preserve original stat name from API for filtering
    projection: outcome.projection,
    offerId: outcome.offerId, // Preserve offerId for EV recalculation
    overProbabilities: outcome.overProbabilities, // Feed's P(over) grid, echoed back so custom EV matches the table
    sportsbooks,
    // Teaser rows (non-subscribers): the server already removed the values; keep the flag + rank.
    ...(outcome.locked ? { locked: true, lockedRank: outcome.lockedRank } : {}),
  };
}

export function transformPropsApiToGames(response: PropsApiResponse, league: string = 'NBA'): DisplayGame[] {
  const gameMap = new Map<string, DisplayGame>();
  const playerPropsMap = new Map<string, Map<string, { outcome: PropsOutcome; bestSportsbook: SportsbookOutcome | null; allSportsbooks: SportsbookOutcome[] }>>();

  for (const outcome of response.outcomes) {
    const { awayTeam, homeTeam } = parseMatchup(outcome.matchup, league);
    const gameId = generateGameId(awayTeam, homeTeam);
    const playerId = generatePlayerId(outcome.playerName);
    const normalizedStat = normalizeStat(outcome.stat, league);

    const side = outcome.label as 'Over' | 'Under';
    const statKey = `${normalizedStat}:${side}`;

    // Create game if it doesn't exist
    if (!gameMap.has(gameId)) {
      const awayTeamInfo = getTeamInfoForLeague(awayTeam, league);
      const homeTeamInfo = getTeamInfoForLeague(homeTeam, league);

      gameMap.set(gameId, {
        id: gameId,
        time: formatEventTime(outcome.eventStartTime, league),
        startTime: outcome.eventStartTime, // Store ISO timestamp for sorting
        matchup: `${awayTeam} @ ${homeTeam}`,
        count: '0',
        awayTeam: { name: awayTeamInfo.name, abbr: awayTeam, logo: awayTeamInfo.logo },
        homeTeam: { name: homeTeamInfo.name, abbr: homeTeam, logo: homeTeamInfo.logo },
        total: 'N/A',
        spread: 'N/A',
        players: [],
      });
    }

    // Find best sportsbook for this outcome (may be null if no sportsbooks have markets)
    const bestSportsbook = findBestSportsbook(outcome.sportsbooks);

    // Track best prop per player+stat combination
    // Include props even without sportsbooks to preserve projection data
    const playerKey = `${gameId}:${playerId}`;
    if (!playerPropsMap.has(playerKey)) {
      playerPropsMap.set(playerKey, new Map());
    }

    const playerProps = playerPropsMap.get(playerKey)!;
    const existingProp = playerProps.get(statKey);

    // Keep the prop with highest EV, merge sportsbooks for same stat+side
    if (!existingProp) {
      playerProps.set(statKey, {
        outcome,
        bestSportsbook,
        allSportsbooks: [...outcome.sportsbooks]
      });
    } else if (bestSportsbook && (!existingProp.bestSportsbook || bestSportsbook.ev > existingProp.bestSportsbook.ev)) {
      // Update best sportsbook but keep all sportsbooks merged
      const mergedSportsbooks = [...existingProp.allSportsbooks];
      for (const sb of outcome.sportsbooks) {
        if (!mergedSportsbooks.find(existing => existing.code === sb.code)) {
          mergedSportsbooks.push(sb);
        }
      }
      playerProps.set(statKey, {
        outcome,
        bestSportsbook,
        allSportsbooks: mergedSportsbooks
      });
    } else {
      // Keep existing best but merge sportsbooks
      const mergedSportsbooks = [...existingProp.allSportsbooks];
      for (const sb of outcome.sportsbooks) {
        if (!mergedSportsbooks.find(existing => existing.code === sb.code)) {
          mergedSportsbooks.push(sb);
        }
      }
      existingProp.allSportsbooks = mergedSportsbooks;
    }
  }

  // Build players for each game
  const playerDataMap = new Map<string, PlayerData>();

  Array.from(playerPropsMap.entries()).forEach(([playerKey, propsMap]) => {
    const [gameId, playerId] = playerKey.split(':');
    const game = gameMap.get(gameId);
    if (!game) return;

    // Get first outcome to extract player info
    const propsArray = Array.from(propsMap.values());
    const firstProp = propsArray[0];
    if (!firstProp) return;

    const { outcome } = firstProp;

    // Create or get player
    const fullPlayerKey = `${gameId}:${playerId}`;
    if (!playerDataMap.has(fullPlayerKey)) {
      playerDataMap.set(fullPlayerKey, {
        id: playerId,
        name: outcome.playerName,
        team: getTeamAbbr(outcome.team, league),
        position: outcome.position || '',
        minutes: outcome.minutes ? outcome.minutes.toFixed(1) : '0',
        // Every stat the projection file has for this player, keyed by display code — including stats no
        // book prices, so the card and the rate editor never fall back to a market-shaped hole.
        projectionSet: outcome.playerProjections
          ? Object.fromEntries(
              Object.entries(outcome.playerProjections).map(([stat, value]) => [normalizeStat(stat, league), value])
            )
          : undefined,
        avatar: outcome.playerHeadshot || DEFAULT_AVATAR,
        injuryStatus: outcome.injuryStatus,
        injuryNotes: outcome.injuryNotes,
        battingOrder: outcome.battingOrder,
        confirmedLineup: outcome.confirmedLineup,
        hand: outcome.hand,
        projections: {
          pts: '0.0',
          reb: '0.0',
          ast: '0.0',
          pra: '0.0',
          threes: '0.0',
        },
        props: [],
      });
    }

    const player = playerDataMap.get(fullPlayerKey)!;

    // Add props and update projections
    Array.from(propsMap.entries()).forEach(([statKey, { outcome: propOutcome, allSportsbooks }]) => {
      const prop = createPropFromOutcome(propOutcome, allSportsbooks, league);
      player.props.push(prop);

      // Update projection value for this stat (extract stat from statKey "PTS:Over" -> "PTS")
      const statOnly = statKey.split(':')[0];
      const projKey = STAT_TO_PROJ_KEY[statOnly];
      // Locked teaser rows carry no projection (stripped server-side).
      if (projKey && typeof propOutcome.projection === 'number') {
        player.projections[projKey] = propOutcome.projection.toFixed(getStatDecimals(statOnly, league));
      }
    });

    // Sort props by best EV (highest first)
    player.props.sort((a, b) => {
      const aEv = a.sportsbooks[0]?.evPct ?? -Infinity;
      const bEv = b.sportsbooks[0]?.evPct ?? -Infinity;
      return bEv - aEv;
    });

    // Add player to game if not already added
    if (!game.players.find(p => p.id === player.id)) {
      game.players.push(player);
    }
  });

  // Update player counts and infer pitcher battingOrder from stats
  const games = Array.from(gameMap.values());
  for (const game of games) {
    game.count = game.players.length.toString();

    // For MLB: if a player has pitcher-specific stats and no battingOrder, infer 0
    if (league === 'MLB') {
      for (const player of game.players) {
        if (player.battingOrder === undefined && player.props.some(p => PITCHER_STATS.has(p.stat))) {
          player.battingOrder = 0;
        }
      }
    }
  }

  // Sort games by event start time (earliest first)
  games.sort((a, b) => {
    const dateA = new Date(a.startTime).getTime();
    const dateB = new Date(b.startTime).getTime();
    return dateA - dateB;
  });

  return games;
}

export function getAvailableSportsbooksFromResponse(response: PropsApiResponse): string[] {
  const books = new Set<string>();
  for (const outcome of response.outcomes) {
    for (const sportsbook of outcome.sportsbooks) {
      books.add(sportsbook.code);
    }
  }
  return Array.from(books).sort();
}
